import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FlaskConical, Play, RefreshCw, Download, Scale } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Cell, ComposedChart, Area
} from "recharts";
import StrikePhilosophy from "./StrikePhilosophy";

// Iter217 — visible strike-rule disclosure per strategy. For strategies in
// OTM_IGNORED the %OTM input does nothing, so it is disabled with the rule shown.
const OTM_IGNORED = new Set(["bull_call_spread", "iron_condor", "bear_put_spread", "bear_call_spread", "straddle", "strangle", "long_stock"]);
// Iter218 — strategies with a vertical spread width the user may override
const WIDTH_STRATS = new Set(["bull_put_spread", "bull_call_spread", "bear_put_spread", "bear_call_spread"]);
// Iter219 — strategies supporting a delta target for the long protective wing
const LONG_DELTA_STRATS = new Set(["bull_put_spread"]);
const STRIKE_RULES = {
  bull_put_spread: "Short put at spot −%OTM (or short Δ target); long put one price-scaled width (~2% of spot) below (or at long Δ target)",
  cash_secured_put: "Short put at spot −%OTM (or target Δ)",
  covered_call: "Short call at spot +%OTM (or target Δ)",
  bull_call_spread: "%OTM not used — long call at ATM, short call one price-scaled width (~2% of spot) above",
  iron_condor: "%OTM not used — short strikes at spot ±7%, wings ~1% of spot",
  bear_put_spread: "%OTM not used — long put at ATM, short put one price-scaled width below",
  bear_call_spread: "%OTM not used — short call at spot +2%, long call one price-scaled width above",
  collar: "Long put at spot −%OTM, short call at spot +%OTM (or both at target Δ)",
  protective_put: "Long put at spot −%OTM (or target Δ)",
  long_call: "Long call at spot +(%OTM ÷ 2) (or target Δ)",
  long_put: "Long put at spot −(%OTM ÷ 2) (or target Δ)",
  straddle: "%OTM not used — both legs at ATM",
  strangle: "%OTM not used — legs at spot ±3%",
  long_stock: "No option legs — %OTM not used",
};

const STRATEGIES = [
  { value: "bull_put_spread", label: "Bull Put Spread" },
  { value: "cash_secured_put", label: "Cash-Secured Put" },
  { value: "covered_call", label: "Covered Call" },
  { value: "bull_call_spread", label: "Bull Call Spread" },
  { value: "iron_condor", label: "Iron Condor" },
  { value: "bear_put_spread", label: "Bear Put Spread" },
  { value: "bear_call_spread", label: "Bear Call Spread" },
  { value: "collar", label: "Collar" },
  { value: "protective_put", label: "Protective Put" },
  { value: "long_call", label: "Long Call" },
  { value: "long_put", label: "Long Put" },
  { value: "straddle", label: "Straddle" },
  { value: "strangle", label: "Strangle" },
  { value: "long_stock", label: "Long Stock" },
];

const ENTRY_SIGNALS = [
  { value: "any", label: "Any (periodic)" },
  { value: "rsi_oversold", label: "RSI Oversold (<35)" },
  { value: "rsi_overbought", label: "RSI Overbought (>70)" },
  { value: "pullback", label: "Pullback to SMA" },
  { value: "macd_bullish_cross", label: "MACD Bullish Cross" },
  { value: "macd_bearish_cross", label: "MACD Bearish Cross" },
  { value: "bb_lower_touch", label: "BB Lower Touch" },
  { value: "bb_upper_touch", label: "BB Upper Touch" },
  { value: "volume_spike", label: "Volume Spike (>1.5x)" },
  { value: "above_sma50", label: "Above SMA50" },
  { value: "below_sma50", label: "Below SMA50" },
];

const EXIT_RULES = [
  { value: "hold_to_expiry", label: "Hold to Expiry" },
  { value: "profit_target", label: "Profit Target" },
];

const PERIODS = [
  { value: "1y", label: "1 Year" },
  { value: "2y", label: "2 Years" },
  { value: "5y", label: "5 Years" },
  { value: "max", label: "Max" },
];

// Native select with consistent dark/light theme styling
function NativeSelect({ value, onChange, options, testId, label }) {
  return (
    <div>
      {label && <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>{label}</Label>}
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-8 px-2 text-xs font-mono-data rounded-sm border outline-none appearance-none cursor-pointer"
        style={{
          background: "var(--hoip-bg)",
          borderColor: "var(--hoip-border)",
          color: "var(--hoip-text-primary)",
        }}
      >
        {options.map(o => (
          <option key={o.value} value={o.value} style={{ background: "var(--hoip-card)", color: "var(--hoip-text-primary)" }}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function BacktestLab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Iter142: "Strike Selection Philosophy" tab is admin-only (distinct
  // from the Pro tier gate on other pages). Non-admin users don't see
  // the tab button, and defensively can't reach the mode either.
  const [mode, setMode] = useState("standard"); // standard | philosophy
  const [ticker, setTicker] = useState("SPY");
  const [strategy, setStrategy] = useState("bull_put_spread");
  const [entrySignal, setEntrySignal] = useState("any");
  const [exitRule, setExitRule] = useState("hold_to_expiry");
  const [period, setPeriod] = useState("2y");
  const [pctOtm, setPctOtm] = useState(5);
  const [targetDelta, setTargetDelta] = useState("");
  const [longDelta, setLongDelta] = useState("");
  const [spreadWidth, setSpreadWidth] = useState("");
  const [dte, setDte] = useState(30);
  const [profitTarget, setProfitTarget] = useState(50);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Iter214 — one-time notice: premium pricing upgraded to strike-aware Black-Scholes
  const [showPricingNotice, setShowPricingNotice] = useState(() => !localStorage.getItem("mlab_bt_pricing_v2_seen"));
  const dismissPricingNotice = () => { localStorage.setItem("mlab_bt_pricing_v2_seen", "1"); setShowPricingNotice(false); };

  const runBacktest = async () => {
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/backtest/run", {
        ticker: ticker.toUpperCase(),
        strategy,
        period,
        params: {
          pct_otm: pctOtm,
          dte,
          exit_rule: exitRule,
          entry_signal: entrySignal,
          profit_target_pct: profitTarget,
          ...(targetDelta !== "" && !OTM_IGNORED.has(strategy) ? { target_delta: parseFloat(targetDelta) } : {}),
          ...(longDelta !== "" && LONG_DELTA_STRATS.has(strategy) ? { long_delta: parseFloat(longDelta) } : {}),
          ...(spreadWidth !== "" && WIDTH_STRATS.has(strategy) ? { spread_width: parseFloat(spreadWidth) } : {}),
        }
      });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Backtest failed");
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!result?.trades) return;
    const headers = "Entry Date,Exit Date,Entry Price,Strike,Premium,Expiry Price,P&L,Assigned,Days Held\n";
    const rows = result.trades.map(t =>
      `${t.entry_date},${t.exit_date},${t.entry_price},${t.strike},${t.premium},${t.expiry_price},${t.pnl},${t.assigned},${t.days_held}`
    ).join("\n");
    const blob = new Blob([headers + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `backtest_${ticker}_${strategy}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const s = result?.stats || {};
  const fits = result?.distribution_fits || [];

  return (
    <div className="p-3 md:p-6 space-y-3 md:space-y-4" data-testid="backtest-lab">
      <div className="flex items-center gap-2">
        <FlaskConical className="w-5 h-5 text-purple-500" />
        <h1 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Backtest Lab</h1>
      </div>
      <p className="text-[11px] leading-relaxed" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="bt-methodology-note">
        Methodology: underlying prices are real historical data; option premiums are strike-aware Black-Scholes estimates,
        not historical chain prices. Informational only — hypothetical results do not guarantee future performance.
      </p>

      {showPricingNotice && (
        <div className="border p-3 flex items-start justify-between gap-3 text-xs" data-testid="bt-pricing-upgrade-notice"
          style={{ background: "rgba(139,92,246,0.08)", borderColor: "rgba(139,92,246,0.35)", color: "var(--hoip-text-secondary)" }}>
          <span>
            <strong style={{ color: "var(--hoip-text-primary)" }}>Backtest accuracy upgrade — multiple corrections.</strong>{" "}
            (1) Option premiums are now strike-aware Black-Scholes estimates (previously flat %-of-spot estimates that did
            not respond to strike distance). (2) Profit-target exits now follow modeled option-value decay instead of a
            linear time proxy. (3) Spread losses are now correctly capped at spread width. Results may differ from
            previously viewed runs — multi-leg strategies (spreads, condors, butterflies) can shift more than single-leg
            ones. Underlying price data is unchanged and remains real historical data.
          </span>
          <button onClick={dismissPricingNotice} data-testid="bt-pricing-notice-dismiss"
            className="shrink-0 text-xs underline" style={{ color: "var(--hoip-text-tertiary)" }}>
            Dismiss
          </button>
        </div>
      )}

      {/* Module toggle */}
      <div className="flex gap-1.5" data-testid="bt-mode-toggle">
        <button onClick={() => setMode("standard")} data-testid="bt-mode-standard"
          className="px-3 h-8 rounded-sm text-xs font-medium border transition-colors flex items-center gap-1.5"
          style={{ borderColor: mode === "standard" ? "var(--hoip-text-primary)" : "var(--hoip-border)", background: mode === "standard" ? "var(--hoip-hover)" : "transparent", color: "var(--hoip-text-primary)" }}>
          <FlaskConical className="w-3.5 h-3.5" /> Standard Backtest
        </button>
        {isAdmin && (
          <button onClick={() => setMode("philosophy")} data-testid="bt-mode-philosophy"
            className="px-3 h-8 rounded-sm text-xs font-medium border transition-colors flex items-center gap-1.5"
            style={{ borderColor: mode === "philosophy" ? "var(--hoip-text-primary)" : "var(--hoip-border)", background: mode === "philosophy" ? "var(--hoip-hover)" : "transparent", color: "var(--hoip-text-primary)" }}>
            <Scale className="w-3.5 h-3.5" /> Strike Selection Philosophy
            <span className="text-[9px] font-bold px-1 py-0.5 rounded-sm ml-1"
              style={{ background: "rgba(139,92,246,0.15)", color: "#A78BFA", border: "1px solid rgba(139,92,246,0.3)" }}>Admin</span>
          </button>
        )}
      </div>

      {mode === "philosophy" && isAdmin && <StrikePhilosophy />}

      {mode === "standard" && (<>
      {/* Parameters */}
      <div className="border p-3 md:p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
        <h2 className="text-xs md:text-sm font-bold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>Parameters</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-2 md:gap-3">
          <div>
            <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Ticker</Label>
            <Input data-testid="bt-ticker" value={ticker} onChange={(e) => setTicker(e.target.value.toUpperCase())}
              className="bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm h-8 text-xs font-mono-data" style={{ color: "var(--hoip-text-primary)" }} />
          </div>
          <NativeSelect value={strategy} onChange={setStrategy} options={STRATEGIES} testId="bt-strategy" label="Strategy" />
          <div>
            <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>
              % OTM{(OTM_IGNORED.has(strategy) || targetDelta !== "") && <span style={{ color: "var(--hoip-text-tertiary)" }}> (not used)</span>}
            </Label>
            <Input data-testid="bt-otm" type="number" value={pctOtm} onChange={(e) => setPctOtm(parseFloat(e.target.value) || 0)}
              disabled={OTM_IGNORED.has(strategy) || targetDelta !== ""}
              title={OTM_IGNORED.has(strategy) ? STRIKE_RULES[strategy] : (targetDelta !== "" ? "Strikes are driven by the target delta" : STRIKE_RULES[strategy])}
              className="bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm h-8 text-xs font-mono-data disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ color: "var(--hoip-text-primary)" }} />
          </div>
          <div>
            <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>
              {LONG_DELTA_STRATS.has(strategy) ? "Short Δ (opt.)" : "Target Δ (opt.)"}
            </Label>
            <Input data-testid="bt-target-delta" type="number" step="0.05" min="0.02" max="0.95" placeholder="e.g. 0.30"
              value={targetDelta} onChange={(e) => setTargetDelta(e.target.value)}
              disabled={OTM_IGNORED.has(strategy)}
              title={OTM_IGNORED.has(strategy) ? STRIKE_RULES[strategy] : "Optional: place the %OTM-driven leg at this |delta| per entry (overrides %OTM)"}
              className="bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm h-8 text-xs font-mono-data disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ color: "var(--hoip-text-primary)" }} />
          </div>
          {LONG_DELTA_STRATS.has(strategy) && (
            <div>
              <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Long Δ (opt.)</Label>
              <Input data-testid="bt-long-delta" type="number" step="0.05" min="0.01" max="0.90" placeholder="e.g. 0.10"
                value={longDelta} onChange={(e) => setLongDelta(e.target.value)}
                title="Optional: place the long protective wing at this |delta| per entry (overrides the width-based placement)"
                className="bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm h-8 text-xs font-mono-data"
                style={{ color: "var(--hoip-text-primary)" }} />
            </div>
          )}
          {WIDTH_STRATS.has(strategy) && (
            <div>
              <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>
                Width $ (opt.){longDelta !== "" && LONG_DELTA_STRATS.has(strategy) && <span style={{ color: "var(--hoip-text-tertiary)" }}> (not used)</span>}
              </Label>
              <Input data-testid="bt-spread-width" type="number" step="0.5" min="0.5" placeholder="auto ~2%"
                value={spreadWidth} onChange={(e) => setSpreadWidth(e.target.value)}
                disabled={longDelta !== "" && LONG_DELTA_STRATS.has(strategy)}
                title={longDelta !== "" && LONG_DELTA_STRATS.has(strategy) ? "The long wing is driven by the long Δ target" : "Optional: fixed dollar distance between the short and long strikes (overrides the automatic ~2%-of-spot width)"}
                className="bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm h-8 text-xs font-mono-data disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ color: "var(--hoip-text-primary)" }} />
            </div>
          )}
          <div>
            <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>DTE</Label>
            <Input data-testid="bt-dte" type="number" value={dte} onChange={(e) => setDte(parseInt(e.target.value) || 30)}
              className="bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm h-8 text-xs font-mono-data" style={{ color: "var(--hoip-text-primary)" }} />
          </div>
          <NativeSelect value={entrySignal} onChange={setEntrySignal} options={ENTRY_SIGNALS} testId="bt-entry-signal" label="Entry Signal" />
          <NativeSelect value={exitRule} onChange={setExitRule} options={EXIT_RULES} testId="bt-exit-rule" label="Exit Rule" />
          <NativeSelect value={period} onChange={setPeriod} options={PERIODS} testId="bt-period" label="Period" />
          {exitRule === "profit_target" && (
            <div>
              <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Profit %</Label>
              <Input type="number" value={profitTarget} onChange={(e) => setProfitTarget(parseInt(e.target.value) || 50)}
                className="bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm h-8 text-xs font-mono-data" style={{ color: "var(--hoip-text-primary)" }} />
            </div>
          )}
        </div>
        {/* Iter217 — always-visible strike rule for the selected strategy */}
        <p className="mt-2 text-[11px]" data-testid="bt-strike-rule"
          style={{ color: OTM_IGNORED.has(strategy) ? "var(--hoip-amber, #f59e0b)" : "var(--hoip-text-tertiary)" }}>
          Strike rule: {STRIKE_RULES[strategy] || "—"}
        </p>
        <div className="flex gap-2 mt-3">
          <Button onClick={runBacktest} disabled={loading} className="h-8 bg-white text-black hover:bg-gray-200 rounded-sm text-xs relative z-10" data-testid="bt-run-btn">
            {loading ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
            {loading ? "Running..." : "Run Backtest"}
          </Button>
          {result && (
            <Button onClick={exportCSV} variant="outline" className="h-8 border-[var(--hoip-border)] text-xs rounded-sm" data-testid="bt-export-btn">
              <Download className="w-3 h-3 mr-1" /> Export CSV
            </Button>
          )}
        </div>
        {error && <p className="text-xs text-red-500 mt-2" data-testid="bt-error">{error}</p>}
        {/* Iter216 — read-only per-leg BS deltas (avg at entry). %OTM stays the input; this lets users reason in delta terms. */}
        {result?.leg_deltas?.length > 0 && (
          <div className="mt-3 pt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5" style={{ borderTop: "1px solid var(--hoip-border)" }} data-testid="bt-leg-deltas">
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--hoip-text-tertiary)" }}>
              Leg deltas (avg at entry)
            </span>
            {result.leg_deltas.map(l => (
              <span key={l.leg + l.strike_rule} className="text-xs font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}
                title={`${l.strike_rule} — range ${l.min_delta} to ${l.max_delta} across entries`}
                data-testid={`bt-delta-${l.leg.toLowerCase().replace(/\s+/g, "-")}`}>
                {l.leg} <strong style={{ color: "var(--hoip-text-primary)" }}>Δ {l.avg_delta > 0 ? "+" : ""}{l.avg_delta.toFixed(2)}</strong>
                <span style={{ color: "var(--hoip-text-tertiary)" }}> · {l.strike_rule}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-3 md:space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {[
              { label: "Trades", value: s.total_trades },
              { label: "Win Rate", value: `${s.win_rate}%`, color: s.win_rate >= 50 ? "var(--hoip-profit)" : "var(--hoip-loss)" },
              { label: "Total P&L", value: `$${s.total_pnl?.toLocaleString()}`, color: s.total_pnl >= 0 ? "var(--hoip-profit)" : "var(--hoip-loss)" },
              { label: "Mean P&L", value: `$${s.mean_pnl}` },
              { label: "Max Win", value: `$${s.max_win}`, color: "var(--hoip-profit)" },
              { label: "Max Loss", value: `$${s.max_loss}`, color: "var(--hoip-loss)" },
              { label: "VaR 95%", value: `$${s.var_95}`, color: "var(--hoip-loss)" },
              { label: "Assign %", value: `${s.assignment_rate}%` },
            ].map(({ label, value, color }) => (
              <div key={label} className="border p-2 md:p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
                <span className="text-[10px] block" style={{ color: "var(--hoip-text-tertiary)" }}>{label}</span>
                <span className="font-mono-data text-xs md:text-sm font-medium" style={{ color: color || "var(--hoip-text-primary)" }}>{value}</span>
              </div>
            ))}
          </div>

          <Tabs defaultValue="equity">
            <TabsList className="bg-[var(--hoip-card)] border border-[var(--hoip-border)] rounded-sm h-9 flex-wrap">
              <TabsTrigger value="equity" className="text-xs rounded-sm data-[state=active]:bg-[var(--hoip-hover)]">Equity Curve</TabsTrigger>
              <TabsTrigger value="distribution" className="text-xs rounded-sm data-[state=active]:bg-[var(--hoip-hover)]">Distribution</TabsTrigger>
              <TabsTrigger value="fits" className="text-xs rounded-sm data-[state=active]:bg-[var(--hoip-hover)]">Dist Fits</TabsTrigger>
              <TabsTrigger value="trades" className="text-xs rounded-sm data-[state=active]:bg-[var(--hoip-hover)]">Trades</TabsTrigger>
            </TabsList>

            <TabsContent value="equity" className="mt-3">
              <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
                <h3 className="text-xs font-bold mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>Cumulative P&L — {strategy.replace(/_/g, " ")} on {ticker}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={result.equity_curve}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                    <XAxis dataKey="date" stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} interval="preserveStartEnd" />
                    <YAxis stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={v => `$${v}`} />
                    <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px", fontFamily: "JetBrains Mono" }}
                      formatter={(v, n) => [`$${v}`, n === "cumulative_pnl" ? "Cumulative" : "Trade"]} />
                    <ReferenceLine y={0} stroke="var(--hoip-border)" />
                    <Area type="monotone" dataKey="cumulative_pnl" stroke="#8B5CF6" fill="rgba(139,92,246,0.15)" strokeWidth={2} dot={false} />
                    <Bar dataKey="trade_pnl" radius={[2, 2, 0, 0]}>
                      {result.equity_curve?.map((e, i) => <Cell key={i} fill={e.trade_pnl >= 0 ? "var(--hoip-profit)" : "var(--hoip-loss)"} />)}
                    </Bar>
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="distribution" className="mt-3">
              <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
                <h3 className="text-xs font-bold mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>P&L Distribution</h3>
                <div className="flex flex-wrap gap-3 mb-2 text-[10px] font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>
                  <span>Skew: {s.skewness}</span><span>Kurt: {s.kurtosis}</span><span>Std: ${s.std_pnl}</span>
                  {fits[0] && <span>Best: <span className="text-purple-400">{fits[0].name}</span></span>}
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={result.histogram}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                    <XAxis dataKey="bin_start" stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={v => `$${v}`} />
                    <YAxis stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} />
                    <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px", fontFamily: "JetBrains Mono" }}
                      formatter={v => [v, "Count"]} />
                    <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                      {result.histogram?.map((e, i) => <Cell key={i} fill={e.bin_start >= 0 ? "var(--hoip-profit)" : "var(--hoip-loss)"} opacity={0.8} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="fits" className="mt-3">
              <div className="border overflow-x-auto" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
                <div className="p-3 border-b" style={{ borderColor: "var(--hoip-border)" }}>
                  <h3 className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Distribution Fitting (MLE) — ranked by AIC</h3>
                </div>
                <table className="w-full dense-table" data-testid="fits-table">
                  <thead>
                    <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                      {["Distribution","AIC","BIC","K-S Stat","K-S p-val","Parameters"].map(h => (
                        <th key={h} className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fits.map((f, i) => (
                      <tr key={i} className={`border-b hover:bg-[var(--hoip-hover)] ${f.best_fit ? "bg-[rgba(139,92,246,0.08)]" : ""}`} style={{ borderColor: "var(--hoip-divider)" }}>
                        <td className="text-xs font-medium capitalize">{f.name.replace(/_/g, " ")}{f.best_fit && <span className="ml-1 text-purple-400 text-[10px]">(best)</span>}</td>
                        <td className="font-mono-data text-xs">{f.aic}</td>
                        <td className="font-mono-data text-xs">{f.bic}</td>
                        <td className="font-mono-data text-xs">{f.ks_stat}</td>
                        <td className="font-mono-data text-xs">{f.ks_pval?.toFixed(4)}</td>
                        <td className="text-xs font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>{Object.entries(f.params || {}).map(([k,v]) => `${k}=${v}`).join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="trades" className="mt-3">
              <div className="border overflow-x-auto" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
                <table className="w-full dense-table" data-testid="bt-trades-table">
                  <thead>
                    <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                      {["Entry","Exit","Price","Strike","Prem","Expiry","P&L","Assgn","Days"].map(h => (
                        <th key={h} className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.trades?.map((t, i) => (
                      <tr key={i} className="border-b hover:bg-[var(--hoip-hover)]" style={{ borderColor: "var(--hoip-divider)" }}>
                        <td className="text-xs font-mono-data">{t.entry_date}</td>
                        <td className="text-xs font-mono-data">{t.exit_date}</td>
                        <td className="text-right font-mono-data text-xs">${t.entry_price?.toFixed(2)}</td>
                        <td className="text-right font-mono-data text-xs">${t.strike?.toFixed(2)}</td>
                        <td className="text-right font-mono-data text-xs">${t.premium?.toFixed(2)}</td>
                        <td className="text-right font-mono-data text-xs">${t.expiry_price?.toFixed(2)}</td>
                        <td className={`text-right font-mono-data text-xs ${t.pnl >= 0 ? "text-emerald-500" : "text-red-500"}`}>${t.pnl?.toFixed(2)}</td>
                        <td className="text-xs text-center">{t.assigned ? <span className="text-yellow-500">Y</span> : "N"}</td>
                        <td className="text-right font-mono-data text-xs">{t.days_held}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}
      </>)}
    </div>
  );
}
